// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/executive/ExecutiveIntelligencePanel.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, StatusChip } from "../../design-system/components";
import "./ExecutiveIntelligencePanel.css";

const EXECUTIVE_PROMPTS = [
  "What should leadership know?",
  "What changed?",
  "Where is risk concentrating?",
  "What deserves attention first?",
];

function normalizeBaseUrl(value) {
  const base = String(value || "").trim();
  return base.endsWith("/") ? base.slice(0, -1) : base;
}

function normalizeCard(value) {
  return value && typeof value === "object" ? value : null;
}


function formatConfidence(value) {
  const normalized = String(value || "unknown").trim().toLowerCase();

  if (normalized === "high") return "High confidence";
  if (normalized === "medium") return "Medium confidence";
  if (normalized === "low") return "Low confidence";
  return "Confidence unavailable";
}

function confidenceTone(value) {
  const normalized = String(value || "").trim().toLowerCase();

  if (normalized === "high") return "success";
  if (normalized === "medium") return "informational";
  if (normalized === "low") return "warning";
  return "neutral";
}

function evidenceLabel(item = {}) {
  return (
    item.item_label ||
    item.label ||
    item.id ||
    item.type ||
    "Executive context"
  );
}

function sourceCountLabel(item = {}) {
  const count = Number(item.item_count);
  if (Number.isFinite(count) && count > 0) {
    return `${count} represented`;
  }

  const records = Number(item.records);
  if (Number.isFinite(records) && records > 0) {
    return `${records} loaded`;
  }

  return "";
}

function ExecutiveSkeleton() {
  return (
    <div
      className="executive-ai-panel__skeleton"
      aria-label="Loading Executive Intelligence"
      role="status"
    >
      <span />
      <span />
      <span />
    </div>
  );
}

export default function ExecutiveIntelligencePanel({
  apiBaseUrl,
  getAccessToken,
  tenantId,
  userId,
  framework,
  windowValue = "30d",
}) {
  const [card, setCard] = useState(null);
  const [activeQuestion, setActiveQuestion] = useState(EXECUTIVE_PROMPTS[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

    const [detailsOpen, setDetailsOpen] = useState(false);
  const requestRef = useRef(null);

  const normalizedBase = useMemo(
    () => normalizeBaseUrl(apiBaseUrl),
    [apiBaseUrl]
  );

  const scopeLabel = useMemo(() => {
    const frameworkLabel = String(framework || "").trim() || "Organization";
    return `${frameworkLabel} · ${windowValue}`;
  }, [framework, windowValue]);

  const ask = useCallback(
    async (question) => {
      if (!normalizedBase || typeof getAccessToken !== "function") {
        setError("Executive Intelligence is not available because API authentication is not configured.");
        return;
      }

      if (requestRef.current) {
        requestRef.current.abort("executive-intelligence-superseded");
      }

      const controller = new AbortController();
      requestRef.current = controller;

      setLoading(true);
      setError("");
      setActiveQuestion(question);

      try {
        const token = await getAccessToken();
        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        };

        if (tenantId) headers["X-Tenant-Id"] = tenantId;
        if (userId) headers["X-User-Id"] = userId;

        const response = await fetch(`${normalizedBase}/ai/copilot/ask`, {
          method: "POST",
          headers,
          signal: controller.signal,
          body: JSON.stringify({
            question,
            scope: "executive",
            copilot_context: {
              page: "executive",
              surface: "executive",
              intelligence_mode: "page",
              framework: framework || undefined,
              window: windowValue,
            },
          }),
        });

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          const detail =
            payload?.detail ||
            payload?.message ||
            `Executive Intelligence request failed (${response.status}).`;
          throw new Error(detail);
        }

        const nextCard = normalizeCard(payload?.card);
        if (!nextCard) {
          throw new Error("Executive Intelligence returned no grounded response card.");
        }

        setCard(nextCard);
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error("Executive ambient intelligence error:", err);
        setError(
          String(
            err?.message ||
              err ||
              "Unable to load Executive Intelligence."
          )
        );

      } finally {
        if (requestRef.current === controller) {
          requestRef.current = null;
          setLoading(false);
        }
      }
    },
    [
      framework,
      getAccessToken,
      normalizedBase,
      tenantId,
      userId,
      windowValue,
    ]
  );

  useEffect(() => {
    ask(EXECUTIVE_PROMPTS[0]);

    return () => {
      if (requestRef.current) {
        requestRef.current.abort("executive-intelligence-unmounted");
        requestRef.current = null;
      }
    };
  }, [ask]); // ask changes when the active Executive context changes.

  const recommendations = Array.isArray(card?.recommended_actions)
    ? card.recommended_actions
    : [];
  const evidence = Array.isArray(card?.evidence_used)
    ? card.evidence_used
    : [];
  const limitations = Array.isArray(card?.known_limitations)
    ? card.known_limitations
    : [];

  const metadata =
    card?.metadata && typeof card.metadata === "object"
      ? card.metadata
      : {};
  const interactionId = String(metadata?.interaction_id || "").trim();
  const auditPersisted = metadata?.audit_persisted === true;
  const responseMode = String(metadata?.ai_response_mode || "").trim();

  return (
    <section
      className="executive-ai-panel"
      aria-labelledby="executive-ai-panel-title"
    >
      <div className="executive-ai-panel__header">
        <div className="executive-ai-panel__heading">
          <div className="executive-ai-panel__eyebrow">
            OASYS INTELLIGENCE
          </div>
          <h2 id="executive-ai-panel-title">Leadership Intelligence</h2>
          <p>
            Grounded reasoning across the selected Executive scope. Deterministic
            OASYS analytics and leadership-action authority remain authoritative.
          </p>
        </div>

        <div className="executive-ai-panel__header-meta">
          <div className="executive-ai-panel__status">
            <StatusChip
              tone={confidenceTone(card?.confidence)}
              dot={Boolean(card)}
            >
              {loading
                ? "Reasoning…"
                : card
                ? formatConfidence(card.confidence)
                : "Awaiting context"}
            </StatusChip>

            {auditPersisted === true ? (
              <StatusChip tone="success">Audited</StatusChip>
            ) : null}

            <Button
              variant="ghost"
              size="small"
              onClick={() => setDetailsOpen((current) => !current)}
              aria-expanded={detailsOpen}
              aria-label={
                detailsOpen
                  ? "Hide intelligence grounding details"
                  : "Show intelligence grounding details"
              }
              className="executive-ai-panel__trust-details"
            >
              {detailsOpen ? "Hide details" : "Details"}
            </Button>
          </div>

        <div className="executive-ai-panel__scope">
          <span>Scope</span>
          <strong>{scopeLabel}</strong>
        </div>
        </div>
      </div>

      <div
        className="executive-ai-panel__prompts"
        role="group"
        aria-label="Executive Intelligence questions"
      >
        {EXECUTIVE_PROMPTS.map((question) => (
          <button
            key={question}
            type="button"
            className={`executive-ai-panel__prompt ${
              activeQuestion === question ? "is-active" : ""
            }`}
            onClick={() => ask(question)}
            disabled={loading}
            aria-pressed={activeQuestion === question}
          >
            {question}
          </button>
        ))}
      </div>

      <div className="executive-ai-panel__body" aria-live="polite">
        {!card && loading ? (
          <ExecutiveSkeleton />
        ) : (
          <>
            {card ? (
              <div className="executive-ai-panel__answer">
                <div className="executive-ai-panel__answer-topline">
                  <span>Leadership brief</span>
                  {loading ? (
                    <span className="executive-ai-panel__refreshing">
                      Refreshing…
                    </span>
                  ) : null}
                </div>

                <div className="executive-ai-panel__summary">
                  {card.summary}
                </div>

                {card.reasoning ? (
                  <p className="executive-ai-panel__reasoning">
                    {card.reasoning}
                  </p>
                ) : null}

                {recommendations.length ? (
                  <div className="executive-ai-panel__actions">
                    <div className="executive-ai-panel__section-label">
                      Leadership attention
                    </div>
                    <div className="executive-ai-panel__action-grid">
                      {recommendations.slice(0, 4).map((item, index) => (
                        <div
                          className="executive-ai-panel__action"
                          key={`${String(item)}-${index}`}
                        >
                          <span>{index + 1}</span>
                          <p>{String(item)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {error ? (
              <div className="executive-ai-panel__error" role="alert">
                {error}
              </div>
            ) : null}
          </>
        )}
      </div>

      {detailsOpen ? (
        <div className="executive-ai-panel__details">
          <div className="executive-ai-panel__details-body">
              <dl className="executive-ai-panel__audit-grid">
                <div>
                  <dt>Audit</dt>
                  <dd>{auditPersisted ? "Persisted" : "Pending"}</dd>
                </div>
                <div>
                  <dt>Response mode</dt>
                  <dd>{responseMode || "—"}</dd>
                </div>
                <div>
                  <dt>Interaction</dt>
                  <dd title={interactionId}>{interactionId || "—"}</dd>
                </div>
              </dl>

              {evidence.length ? (
                <div className="executive-ai-panel__detail-section">
                  <div className="executive-ai-panel__section-label">
                    Evidence used
                  </div>
                  <ul>
                    {evidence.map((item, index) => {
                      const count = sourceCountLabel(item);
                      return (
                        <li key={`${item?.id || item?.type || "evidence"}-${index}`}>
                          <span>{evidenceLabel(item)}</span>
                          {count ? <small>{count}</small> : null}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}

              {limitations.length ? (
                <div className="executive-ai-panel__detail-section">
                  <div className="executive-ai-panel__section-label">
                    Known limitations
                  </div>
                  <ul>
                    {limitations.map((item, index) => (
                      <li key={`${String(item)}-${index}`}>
                        <span>{String(item)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
        </div>
      ) : null}
    </section>
  );
}
